/** @jsx isolateComponent */

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { Interpolation } from '@emotion/css';

import { useLink } from '@zapier/common-hooks';
import { useTrackingContext } from '@zapier/toolbox-tracking';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { useClickOutside } from '@zapier/design-system';
import { ServiceTypeahead } from '@zapier/design-system-patterns';
import {
  searchHasNoResults,
  searchHasResults,
  searchResultClick,
} from '../../constants';
import { useUniversalNavContext } from '../../hooks';

import SearchToggler from './SearchToggler';

export type Props = {
  /*
   * Indicates whether the user is currently searching.
   */
  isSearching: boolean;
  /*
   * The `id` attribute of the search `input`, used for hooking
   * `input` and `label` together.
   */
  searchInputId: string;
  /*
   * Dictates what happens when searching is closed.
   */
  onClose: () => void;
};

const Styles = labelStyles('SearchForm', {
  root: () => [
    {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: 10,
      width: '100%',
      maxWidth: 500,
    },
  ],
});

/**
 * @private
 * Renders a `ServiceTypehead` to allow users to search for apps.
 */
const SearchForm = (props: Props) => {
  const { useQuery } = useUniversalNavContext();
  const { trackEvent } = useTrackingContext();
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => props.onClose(),
  });
  const onEscape = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      props.onClose();
    }
  };
  const { routeTo } = useLink();
  return (
    <div css={Styles.root() as Interpolation}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div onKeyUp={onEscape} ref={setClickOutsideNode}>
        <ServiceTypeahead
          ariaLabel="Search Apps"
          excludeUpcoming={false}
          id="universalNavServiceTypeahead"
          inputId={props.searchInputId}
          menuAriaLabel="List of available apps"
          onChange={(app) => {
            if (app && app.slug && routeTo) {
              const url = `/apps/${app.slug}/integrations`;
              trackEvent(searchResultClick, { slug: app.slug });
              routeTo(url);
            }
          }}
          onResultsUpdate={(query, results) => {
            trackEvent(results.length ? searchHasResults : searchHasNoResults, {
              searchTerm: query,
            });
          }}
          placeholder="Search by App"
          resultsLimit={8}
          useQuery={useQuery}
        />
      </div>
      <SearchToggler
        ariaHidden={!props.isSearching}
        ariaLabel="Close search"
        disabled={!props.isSearching}
        isSearching={true}
        onClick={() => {
          props.onClose();
        }}
      />
    </div>
  );
};

export default SearchForm;
